import { Checkbox } from "@/modern/components/ui/checkbox";
import { cn } from "@/modern/utils";

interface FileDayGroupHeaderProps {
  label: string;
  count: number;
  className?: string;
  /** When set, shows a CompanyCam-style day checkbox for multi-select. */
  selectionChecked?: boolean | "indeterminate";
  onSelectionChange?: () => void;
  selectionDisabled?: boolean;
}

/** Sticky day rail for Media manage — answers “which day?” while scrolling. */
export function FileDayGroupHeader({
  label,
  count,
  className,
  selectionChecked,
  onSelectionChange,
  selectionDisabled = false,
}: FileDayGroupHeaderProps) {
  const countLabel = count === 1 ? "1 photo" : `${count.toLocaleString()} photos`;
  const showSelection = typeof onSelectionChange === "function";

  return (
    <div
      className={cn(
        "sticky top-0 z-[1] mb-3 flex w-full min-w-0 items-center justify-between gap-3",
        "relative border-b border-border/40 bg-background py-2.5",
        className,
      )}
    >
      {/* Opaque cap above the sticky seam so scrolling tiles cannot peek through. */}
      <div
        aria-hidden
        className="pointer-events-none absolute inset-x-0 bottom-full h-4 bg-background"
      />
      <div className="flex min-w-0 flex-1 items-center gap-2.5">
        {showSelection ? (
          <Checkbox
            checked={selectionChecked ?? false}
            disabled={selectionDisabled || count === 0}
            onCheckedChange={() => onSelectionChange?.()}
            aria-label={`Select loaded photos for ${label}`}
            className="shrink-0"
          />
        ) : null}
        <h2 className="min-w-0 truncate text-xl font-normal tracking-tight text-foreground">
          {label}
        </h2>
      </div>
      <span className="shrink-0 text-sm font-normal tabular-nums text-muted-foreground">
        {countLabel}
      </span>
    </div>
  );
}
